if (!window.Eurus.loadedScript.includes('cdc-boost-page-recommendations.js')) {
  window.Eurus.loadedScript.push('cdc-boost-page-recommendations.js');

  const cdcPageRecommendationStorefrontRoot = () => {
    const routeRoot = window.Shopify?.routes?.root || '/';
    return routeRoot.endsWith('/') ? routeRoot : routeRoot + '/';
  };

  const cdcPageRecommendationFormatPrice = (amount) => {
    if (amount === null || amount === undefined || amount === '') return '';
    const normalizedAmount = Number(amount);
    if (!Number.isFinite(normalizedAmount)) return '';

    const currency = window.Shopify?.currency?.active;
    const locale = document.documentElement.lang || window.Shopify?.locale;
    if (currency && typeof Intl !== 'undefined' && typeof Intl.NumberFormat === 'function') {
      try {
        return new Intl.NumberFormat(locale || undefined, {
          style: 'currency',
          currency
        }).format(normalizedAmount);
      } catch (error) {
        try {
          return new Intl.NumberFormat(undefined, {
            style: 'currency',
            currency
          }).format(normalizedAmount);
        } catch (fallbackError) {
          // Fall through only when the active currency itself is invalid.
        }
      }
    }
    if (currency) return currency + ' ' + normalizedAmount.toFixed(2);
    return typeof window.Shopify?.formatMoney === 'function'
      ? window.Shopify.formatMoney(Math.round(normalizedAmount * 100))
      : String(normalizedAmount);
  };

  requestAnimationFrame(() => {
    document.addEventListener('alpine:init', () => {
      Alpine.store('xBoostPageRecommendations', {
        instances: {},
        productHtmlCache: new Map(),
        cartPageRequiredProductTags: ['price-below-100'],
        contextFreeRecommendationTypes: ['bestsellers', 'new-arrivals', 'trending-products'],

        init(sectionId, widgetId, pageType, limit, currentProductId, collectionId) {
          if (this.instances[sectionId]) return;
          this.instances[sectionId] = {
            widgetId,
            pageType,
            limit: limit || 6,
            currentProductId: currentProductId || null,
            collectionId: collectionId || null,
            loading: false,
            recommendations: []
          };
          this.load(sectionId);

          if (pageType === 'cart_page') {
            document.addEventListener('eurus:cart:items-changed', () => {
              this.load(sectionId);
            });
          }
        },

        async load(sectionId) {
          const inst = this.instances[sectionId];
          if (!inst || !inst.widgetId) return;
          inst.loading = true;
          inst.recommendations = [];

          const url = 'https://services.mybcapps.com/discovery/recommend/?wid=' + inst.widgetId + '&pg=' + inst.pageType;
          const widgetConfig = this._getWidgetConfig(window.boostWidgetIntegration?.config?.recommendationWidgets, inst.widgetId);
          const productIds = await this._getContextProductIds(inst, widgetConfig);
          const excludedCollectionKeys = this._getExcludedCollectionKeys(widgetConfig);
          const requiredProductTags = this._getRequiredProductTags(widgetConfig, inst);
          const shouldOverfetch = excludedCollectionKeys.size || requiredProductTags.length;
          const requestLimit = shouldOverfetch ? inst.limit * 3 : inst.limit;

          const payload = {
            ...widgetConfig,
            wid: inst.widgetId,
            limit: requestLimit,
            locale: Shopify.locale,
            currency: Shopify.currency.active,
            country: Shopify.country,
            return_all_currency_fields: false,
            currency_rate: Shopify.currency.rate,
            productIds: productIds
          };
          if (inst.collectionId) {
            payload.collectionId = parseInt(inst.collectionId, 10) || inst.collectionId;
          }
          if (requiredProductTags.length) {
            payload.tags = requiredProductTags;
          }

          try {
            const res = await fetch(url, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(payload)
            });
            const data = await res.json();
            const items = data?.products || [];
            const visibleItems = this
              ._filterExcludedCollections(items, excludedCollectionKeys)
              .filter((item) => this._hasRequiredProductTags(item, requiredProductTags))
              .slice(0, inst.limit);

            const enriched = await Promise.all(visibleItems.map((item) => this._enrichItem(item)));
            inst.recommendations = enriched;
          } catch (err) {
            console.error('xBoostPageRecommendations load error:', err);
          } finally {
            inst.loading = false;
          }
        },

        openSelectSize(sectionId, productId) {
          const inst = this.instances[sectionId];
          if (!inst) return;
          const selected = inst.recommendations.find((p) => p.id === productId);
          if (selected && Alpine.store('xPageSelectSizeModal')) {
            Alpine.store('xPageSelectSizeModal').openModal(selected);
          }
        },

        addToCart(variantId) {
          if (Alpine.store('xPageSelectSizeModal')) {
            Alpine.store('xPageSelectSizeModal').addToCart(variantId);
          }
        },

        async _enrichItem(item) {
          const html = await this._fetchProduct(item?.handle);
          let categoryLine = '';
          let badge = '';
          let productName = '';
          let isOutsidersClubRestricted = false;
          let colorSwatches = [];

          if (html) {
            categoryLine = html.querySelector('.category_second_line')?.textContent?.trim() || '';
            badge = html.querySelector('.cdc-rec-badge')?.textContent?.trim() || '';
            productName = html.querySelector('.cdc-rec-product-name')?.textContent?.trim() || '';
            isOutsidersClubRestricted = this._isOutsidersClubRestrictedProduct(html);
            const swatchAnchors = html.querySelectorAll('.cdc-rec-visual-swatches a');
            colorSwatches = Array.from(swatchAnchors).map((a) => ({
              handle: a.getAttribute('data-handle') || '',
              url: a.getAttribute('data-url') || '',
              image: a.getAttribute('data-image') || '',
              title: a.getAttribute('data-title') || ''
            })).filter((s) => s.image);
          }

          return {
            ...item,
            price: this._getPriceText(item),
            compareAtPrice: this._getCompareAtPriceText(item),
            displayName: productName || item.title,
            categoryLine,
            badge,
            isOutsidersClubRestricted,
            colorSwatches
          };
        },

        async _fetchProduct(productHandle) {
          if (!productHandle) return null;
          if (this.productHtmlCache.has(productHandle)) {
            return this.productHtmlCache.get(productHandle);
          }
          try {
            const productUrl = Shopify.routes.root + 'products/' + productHandle;
            const response = await fetch(productUrl + '?section_id=main-product');
            const responseText = await response.text();
            const doc = new DOMParser().parseFromString(responseText, 'text/html');
            this.productHtmlCache.set(productHandle, doc);
            return doc;
          } catch (e) {
            console.error('xBoostPageRecommendations._fetchProduct error:', e);
            return null;
          }
        },

        _isOutsidersClubRestrictedProduct(html) {
          if (!html) return false;
          if (html.querySelector('[data-cdc-outsiders-club-product]')) return true;

          const bodyText = html.body?.textContent || '';
          return (
            bodyText.includes('Sign up or log in to purchase this item')
            || (bodyText.includes('Log in/Sign up') && !!html.querySelector('img[src*="outsiders_club_logo_black"]'))
          );
        },

        async _getContextProductIds(inst, widgetConfig) {
          const recommendationType = widgetConfig?.recommendationType || '';

          if (recommendationType === 'recently-viewed') {
            return this._getRecentlyViewedIds();
          }
          if (this.contextFreeRecommendationTypes.includes(recommendationType)) {
            return [];
          }
          if (inst.pageType === 'product_page') {
            const id = parseInt(inst.currentProductId, 10);
            return Number.isFinite(id) ? [id] : [];
          }
          if (inst.pageType === 'cart_page') {
            return this._getCartProductIds();
          }
          return [];
        },

        _getRecentlyViewedIds() {
          // Send Boost's raw list; Boost itself drops the current product (first entry).
          let raw = [];
          try {
            raw = JSON.parse(localStorage.getItem('boostSDRecentlyViewedProduct') || '[]');
          } catch (e) {
            raw = [];
          }
          if (!Array.isArray(raw)) return [];
          return raw
            .map((id) => parseInt(id, 10))
            .filter((id) => Number.isFinite(id));
        },

        async _getCartProductIds() {
          try {
            const res = await fetch(cdcPageRecommendationStorefrontRoot() + 'cart.js', {
              credentials: 'same-origin',
              cache: 'no-store',
              headers: { Accept: 'application/json' }
            });
            if (!res.ok) throw new Error('Cart request failed: ' + res.status);
            const cart = await res.json();
            return cart.items.map((i) => i.product_id);
          } catch (e) {
            console.error('xBoostPageRecommendations cart fetch error:', e);
            return [];
          }
        },

        _getPriceText(item) {
          const priceSale = item['price_max'];
          return cdcPageRecommendationFormatPrice(priceSale);
        },

        _getCompareAtPriceText(item) {
          const priceSale = item['compare_at_price_max'];
          return cdcPageRecommendationFormatPrice(priceSale);
        },

        _getWidgetConfig(config, widgetId) {
          if (!config) return null;
          for (const pageKey in config) {
            const page = config[pageKey];
            if (page && typeof page === 'object' && widgetId in page) {
              return page[widgetId].params || null;
            }
          }
          return null;
        },

        _getRequiredProductTags(widgetConfig, inst) {
          const tags = [...this._normalizeTags(widgetConfig?.tag)];
          if (inst?.pageType === 'cart_page') {
            tags.push(...this._normalizeTags(this.cartPageRequiredProductTags));
          }

          return [...new Set(tags)];
        },

        _normalizeTags(value) {
          const values = Array.isArray(value) ? value : (typeof value === 'string' ? value.split(',') : [value]);

          return values
            .filter((tag) => tag !== null && tag !== undefined)
            .map((tag) => String(tag).trim().toLowerCase())
            .filter(Boolean);
        },

        _hasRequiredProductTags(item, requiredProductTags) {
          if (!requiredProductTags.length) return true;
          if (item?.tags === undefined && item?.original_tags === undefined) return true;

          const productTags = new Set(this._normalizeTags(item?.tags || item?.original_tags));
          return requiredProductTags.every((tag) => productTags.has(tag));
        },

        _getExcludedCollectionKeys(widgetConfig) {
          const rule = (widgetConfig?.ruleBased || []).find((item) => {
            return item?.type === 'sameCollection' && item.status === true && Array.isArray(item.excluded);
          });
          const excluded = rule?.excluded || [];

          return new Set(excluded.flatMap((collection) => this._getCollectionKeys(collection)));
        },

        _filterExcludedCollections(items, excludedCollectionKeys) {
          if (!excludedCollectionKeys.size) return items;

          return items.filter((item) => {
            const collections = Array.isArray(item?.collections) ? item.collections : [];
            return !collections.some((collection) => {
              return this._getCollectionKeys(collection).some((key) => excludedCollectionKeys.has(key));
            });
          });
        },

        _getCollectionKeys(collection) {
          const keys = [];
          const add = (value) => {
            if (value === null || value === undefined) return;

            const stringValue = String(value).trim();
            if (!stringValue) return;

            keys.push(stringValue.toLowerCase());

            const idMatch = stringValue.match(/\/(\d+)$/);
            if (idMatch) keys.push(idMatch[1]);

            const handle = this._handleize(stringValue);
            if (handle) keys.push(handle);
          };

          if (collection && typeof collection === 'object') {
            add(collection.id);
            add(collection.handle);
            add(collection.title);
            add(collection.label);
            add(collection.value);
            add(collection.key);
            add(collection.collection_id);
            add(collection.collectionId);
          } else {
            add(collection);
          }

          return keys;
        },

        _handleize(value) {
          return String(value)
            .trim()
            .toLowerCase()
            .replace(/&/g, 'and')
            .replace(/[^a-z0-9]+/g, '-')
            .replace(/^-+|-+$/g, '');
        }
      });

      // Keep DOM references outside Alpine's reactive proxy. The optional
      // source button is the native Boost CTA that opened the modal; clicking
      // that same button after changing its metadata preserves Boost's native
      // recommendation/cart lifecycle and analytics context.
      let nativeSourceButton = null;
      let nativeSourceSnapshot = null;
      let nativeAddTimeout = null;

      const clearNativeAddTimeout = () => {
        if (!nativeAddTimeout) return;
        window.clearTimeout(nativeAddTimeout);
        nativeAddTimeout = null;
      };

      const isElement = (value) => {
        return Boolean(value && value.nodeType === 1 && typeof value.click === 'function');
      };

      const errorText = (value) => {
        if (typeof value === 'string') return value;
        if (Array.isArray(value)) return value.filter(Boolean).join(' ');
        if (value && typeof value === 'object') {
          return Object.values(value).flat().filter(Boolean).join(' ');
        }
        return '';
      };

      const captureNativeSource = (button) => {
        nativeSourceButton = isElement(button) ? button : null;
        nativeSourceSnapshot = nativeSourceButton
          ? {
              metadata: nativeSourceButton.getAttribute('data-metadata'),
              nativeAddFlag: nativeSourceButton.getAttribute('data-cdc-native-add'),
              hadAnalyticsClass: nativeSourceButton.classList.contains('boost-sd__btn-add-to-cart'),
              disabled: nativeSourceButton.disabled
            }
          : null;
      };

      const encodeNativeMetadata = (metadata) => {
        const encoded = JSON.stringify(metadata);
        if (!nativeSourceSnapshot || typeof nativeSourceSnapshot.metadata !== 'string') {
          return encoded;
        }

        try {
          // Boost's json_encode may leave the captured object one JSON-string
          // layer deeper. Preserve the source button's exact encoding contract.
          return typeof JSON.parse(nativeSourceSnapshot.metadata) === 'string'
            ? JSON.stringify(encoded)
            : encoded;
        } catch (error) {
          return encoded;
        }
      };

      const restoreNativeSource = (detach = false) => {
        clearNativeAddTimeout();

        if (nativeSourceButton && nativeSourceSnapshot) {
          if (nativeSourceSnapshot.metadata === null) {
            nativeSourceButton.removeAttribute('data-metadata');
          } else {
            nativeSourceButton.setAttribute('data-metadata', nativeSourceSnapshot.metadata);
          }

          if (nativeSourceSnapshot.nativeAddFlag === null) {
            nativeSourceButton.removeAttribute('data-cdc-native-add');
          } else {
            nativeSourceButton.setAttribute('data-cdc-native-add', nativeSourceSnapshot.nativeAddFlag);
          }

          if (nativeSourceSnapshot.hadAnalyticsClass) {
            nativeSourceButton.classList.add('boost-sd__btn-add-to-cart');
          } else {
            nativeSourceButton.classList.remove('boost-sd__btn-add-to-cart');
          }
          nativeSourceButton.disabled = nativeSourceSnapshot.disabled;
        }

        if (detach) {
          nativeSourceButton = null;
          nativeSourceSnapshot = null;
        }
      };

      Alpine.store('xPageSelectSizeModal', {
        open: false,
        loading: false,
        selectedProduct: null,
        selectedVariant: null,
        errorMessage: '',

        openModal(selectedProduct, sourceButton = null) {
          restoreNativeSource(true);
          captureNativeSource(sourceButton);
          this.selectedProduct = selectedProduct;
          this.selectedVariant = null;
          this.errorMessage = '';
          this.loading = false;
          this.open = true;
        },

        closeModal() {
          restoreNativeSource(true);
          this.selectedProduct = null;
          this.selectedVariant = null;
          this.errorMessage = '';
          this.loading = false;
          this.open = false;
        },

        selectVariant(variant) {
          if (!variant || !variant.available) return;
          this.selectedVariant = variant;
        },

        confirmAddToCart() {
          if (!this.selectedVariant || this.loading) return;

          if (nativeSourceButton && nativeSourceSnapshot) {
            const addToCart = {
              productId: String(this.selectedVariant.id)
            };
            const sellingPlan = this.selectedVariant.preorder?.sellingPlan;
            if (sellingPlan?.id) addToCart.selling_plan = String(sellingPlan.id);

            this.loading = true;
            this.errorMessage = '';
            nativeSourceButton.setAttribute('data-cdc-native-add', 'pending');
            nativeSourceButton.setAttribute('data-metadata', encodeNativeMetadata({
              action: { addToCart }
            }));
            nativeSourceButton.classList.add('boost-sd__btn-add-to-cart');

            // Fail open if an integration error prevents Boost from reporting a
            // terminal button state. A retry will still use the native button.
            clearNativeAddTimeout();
            nativeAddTimeout = window.setTimeout(() => {
              this.handleNativeButtonState(nativeSourceButton, 'failed', 'Unable to add this item. Please try again.');
            }, 15000);

            try {
              nativeSourceButton.click();
            } catch (error) {
              this.handleNativeButtonState(nativeSourceButton, 'failed', error?.message);
            }
            return;
          }

          this.addToCart(this.selectedVariant.id);
        },

        isNativeSourceButton(button) {
          return Boolean(nativeSourceButton && button === nativeSourceButton);
        },

        handleNativeButtonState(button, state, customText = '') {
          if (!this.isNativeSourceButton(button)) return false;
          const normalizedState = String(state || '').toLowerCase();

          if (normalizedState === 'adding') {
            this.loading = true;
            this.errorMessage = '';
            return true;
          }

          if (normalizedState === 'added') {
            this.handleNativeCartOpened();
            return true;
          }

          if (normalizedState === 'failed') {
            restoreNativeSource(false);
            this.loading = false;
            this.errorMessage = errorText(customText) || 'Unable to add this item. Please try again.';
            return true;
          }

          return true;
        },

        // CartController.openCart() is only reached after a successful native
        // add. customization.js calls this before handing off to Eurus's drawer.
        handleNativeCartOpened() {
          if (!nativeSourceButton) return false;
          this.closeModal();
          return true;
        },

        addToCart(variantId, quantity = 1) {
          this.loading = true;
          this.errorMessage = '';
          const sectionsToRender = (Alpine.store('xCartHelper')?.getSectionsToRender() || []);
          fetch(cdcPageRecommendationStorefrontRoot() + 'cart/add.js', {
            method: 'POST',
            credentials: 'same-origin',
            headers: {
              'Content-Type': 'application/json',
              'Accept': 'application/json'
            },
            body: JSON.stringify({
              items: [{ id: variantId, quantity }],
              sections: sectionsToRender.map((s) => s.id),
              sections_url: window.location.pathname
            })
          })
            .then(async (res) => ({
              ok: res.ok,
              status: res.status,
              response: await res.json()
            }))
            .then(({ ok, status, response }) => {
              if (!ok || response?.status === 422 || response?.errors) {
                this.errorMessage = errorText(response?.description || response?.message || response?.errors)
                  || `Unable to add this item (${status}). Please try again.`;
                return;
              }
              sectionsToRender.forEach((section) => {
                const el = document.querySelector(section.selector);
                if (el && response.sections?.[section.id] && typeof getSectionInnerHTML === 'function') {
                  el.innerHTML = getSectionInnerHTML(response.sections[section.id], section.selector);
                }
              });
              this.closeModal();
              if (Alpine.store('xPopup')) {
                Alpine.store('xPopup').open = false;
              }
              if (Alpine.store('xMiniCart')) {
                Alpine.store('xMiniCart').openCart();
              }
              const cartCount = parseInt(document.querySelector('#cart-icon-bubble span')?.textContent, 10);
              if (Number.isFinite(cartCount) && Alpine.store('xCartHelper')) {
                Alpine.store('xCartHelper').currentItemCount = cartCount;
              }
              document.dispatchEvent(new CustomEvent('eurus:cart:items-changed'));
            })
            .catch((err) => {
              this.errorMessage = err?.message || 'Unable to add this item. Please try again.';
              console.error('xPageSelectSizeModal addToCart error:', err);
            })
            .finally(() => {
              this.loading = false;
            });
        }
      });
    });
  });
}
